// 訪客 / 造訪識別（AM-185）。報表改由後端 online_visit_log 統計，新舊訪客由後端依 visitor id 判斷。
//
// - visitor id：同一個瀏覽器持久不變，存在 localStorage `istaging_visitor_id`，
//   以 `guest` 參數送給 GET /publish/v1/product/object/{uid}。
//   沒有或不是合法 UUID → 產生新的 UUID v4 並寫回。
//   localStorage 不可用或丟例外（例如沙箱 / 跨站 iframe 封鎖儲存）→ 用只存在記憶體、本次頁面載入內不變的 id，不影響請求。
// - visit id：每次頁面載入產生一個 UUID v4（模組層級，整個頁面載入期間不變），
//   以 `visitId` 參數送給同一支 API，並帶在 GA page_view 的 visit_id，讓後端與 GA 可以對得起來。

export const VISITOR_ID_STORAGE_KEY = "istaging_visitor_id";

// AM-182 舊做法（前端自己判斷新舊訪客）留下的 key，已不再使用，順手清掉
export const LEGACY_VIEWED_OBJECTS_STORAGE_KEY = "istaging_viewed_objects";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = (value) => typeof value === "string" && UUID_PATTERN.test(value);

const getDefaultCrypto = () => (typeof globalThis !== "undefined" ? globalThis.crypto : undefined);

const randomBytes = (cryptoObj) => {
  const bytes = new Uint8Array(16);
  if (typeof cryptoObj?.getRandomValues === "function") {
    try {
      cryptoObj.getRandomValues(bytes);
      return bytes;
    } catch {
      // 退回 Math.random
    }
  }
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = Math.floor(Math.random() * 256);
  }
  return bytes;
};

// crypto.randomUUID 只在 secure context（https / localhost）且較新的瀏覽器才有，
// 沒有就用 getRandomValues（再不行用 Math.random）自己組 UUID v4。
export const generateUuid = (cryptoObj = getDefaultCrypto()) => {
  if (typeof cryptoObj?.randomUUID === "function") {
    try {
      return cryptoObj.randomUUID();
    } catch {
      // 走下面的 fallback
    }
  }
  const bytes = randomBytes(cryptoObj);
  /* eslint-disable no-bitwise */
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  /* eslint-enable no-bitwise */
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// 跨站 iframe 封鎖第三方儲存時，光是讀 window.localStorage 就會丟 SecurityError
const defaultGetStorage = () => window.localStorage;

const safely = (fn) => {
  try {
    return fn();
  } catch {
    return undefined;
  }
};

// 一次頁面載入一個實例。拆成工廠是為了測試可以注入 storage / id 產生器。
export const createVisitIdentity = ({
  getStorage = defaultGetStorage,
  generateId = generateUuid,
} = {}) => {
  const visitId = generateId();
  let visitorId;

  const resolveVisitorId = () => {
    const storage = safely(getStorage);
    const stored = safely(() => storage?.getItem(VISITOR_ID_STORAGE_KEY));
    safely(() => storage?.removeItem(LEGACY_VIEWED_OBJECTS_STORAGE_KEY));
    if (isUuid(stored)) return stored;

    const id = generateId();
    // 寫入失敗時這個 id 只活在記憶體，本次頁面載入內仍維持一致
    safely(() => storage?.setItem(VISITOR_ID_STORAGE_KEY, id));
    return id;
  };

  const getVisitorId = () => {
    if (!visitorId) visitorId = resolveVisitorId();
    return visitorId;
  };

  return { visitId, getVisitorId };
};

const pageIdentity = createVisitIdentity();

export const getVisitorId = () => pageIdentity.getVisitorId();

export const getVisitId = () => pageIdentity.visitId;
